import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// Configuration de l'API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Token en mémoire (backup)
let memoryToken: string | null = null;

export const setMemoryToken = (token: string | null) => {
  memoryToken = token;
  console.log('[API] Memory token set:', token ? 'OK' : 'null');
};

export const getToken = (): string | null => {
  const lsToken = localStorage.getItem('access_token');
  const token = lsToken || memoryToken;
  console.log('[API] getToken - localStorage:', !!lsToken, 'memory:', !!memoryToken, 'using:', !!token);
  return token;
};

// Instance Axios configurée
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 secondes pour les connexions lentes (Afrique)
});

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('[API] Request:', config.method?.toUpperCase(), config.url, '- Token: OK');
    } else {
      console.log('[API] Request:', config.method?.toUpperCase(), config.url, '- Token: MISSING');
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Intercepteur pour gérer les erreurs et le refresh token
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    console.log('[API] Erreur interceptée:', error.response?.status, error.config?.url);
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // Si erreur 401 et pas déjà une tentative de refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      console.log('[API] Tentative de refresh token...');
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
            headers: { Authorization: `Bearer ${refreshToken}` }
          });
          
          const { access_token } = response.data;
          console.log('[API] Refresh réussi, nouveau token obtenu');
          localStorage.setItem('access_token', access_token);
          
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
          }
          return api(originalRequest);
        } catch (refreshError) {
          console.error('[API] Refresh échoué');
          // Ne pas rediriger automatiquement, laisser le composant gérer
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          return Promise.reject(refreshError);
        }
      } else {
        console.log('[API] Pas de refresh token disponible');
      }
    }
    
    return Promise.reject(error);
  }
);

// Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  items: T[];
  total: number;
  page: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

// Services d'authentification
export const authService = {
  register: (data: {
    email: string;
    password: string;
    nom: string;
    prenom: string;
    telephone?: string;
    role: string;
  }) => api.post('/auth/register', data),

  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  verifyEmail: (token: string) =>
    api.post('/auth/verify-email', { token }),

  resendValidation: (email: string) =>
    api.post('/auth/resend-validation', { email }),

  getMe: () => api.get('/auth/me'),

  updateProfile: (data: { nom?: string; prenom?: string; telephone?: string }) =>
    api.put('/auth/me', data),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),

  logout: () => api.post('/auth/logout'),

  registerWithBooking: (data: {
    email: string;
    password: string;
    nom: string;
    prenom: string;
    telephone: string;
    booking: {
      apartment_id: number;
      date_debut: string;
      date_fin: string;
      nb_personnes: number;
      notes?: string;
      is_deposit?: boolean;
    };
  }) => api.post('/auth/register-with-booking', data),
};

// Services appartements
export const apartmentService = {
  getAll: (params?: {
    page?: number;
    per_page?: number;
    ville?: string;
    capacite_min?: number;
    capacite_max?: number;
    prix_min?: number;
    prix_max?: number;
    equipments?: string;
    sort?: string;
  }) => api.get('/apartments', { params }),

  getById: (id: number) => api.get(`/apartments/${id}`),

  getAvailability: (id: number, start?: string, end?: string) =>
    api.get(`/apartments/${id}/availability`, { params: { start, end } }),

  getCities: () => api.get('/apartments/cities'),

  getEquipments: () => api.get('/apartments/equipments'),

  searchAvailable: (params?: {
    ville?: string;
    date_debut?: string;
    date_fin?: string;
    nb_personnes?: number;
    page?: number;
    per_page?: number;
  }) => api.get('/apartments/search', { params }),

  // Routes gérant
  getMyApartments: () => api.get('/apartments/my'),

  create: (data: any) => api.post('/apartments', data),

  update: (id: number, data: any) => api.put(`/apartments/${id}`, data),

  delete: (id: number) => api.delete(`/apartments/${id}`),

  publish: (id: number) => api.post(`/apartments/${id}/publish`),

  // Photos
  uploadPhoto: (apartmentId: number, file: File, isPrincipale?: boolean) => {
    const formData = new FormData();
    formData.append('file', file);
    if (isPrincipale) {
      formData.append('is_principale', 'true');
    }
    return api.post(`/apartments/${apartmentId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deletePhoto: (apartmentId: number, photoId: number) =>
    api.delete(`/apartments/${apartmentId}/photos/${photoId}`),

  setPhotoAsPrincipale: (apartmentId: number, photoId: number) =>
    api.post(`/apartments/${apartmentId}/photos/${photoId}/set-principale`),

  reorderPhotos: (apartmentId: number, photoIds: number[]) =>
    api.post(`/apartments/${apartmentId}/photos/reorder`, { photo_ids: photoIds }),
};

// Services réservations
export const bookingService = {
  checkAvailability: (data: {
    apartment_id: number;
    date_debut: string;
    date_fin: string;
  }) => api.post('/bookings/check-availability', data),

  create: (data: {
    apartment_id: number;
    date_debut: string;
    date_fin: string;
    nb_personnes: number;
    notes?: string;
    is_deposit?: boolean;
  }) => api.post('/bookings', data),

  createGuest: (data: {
    apartment_id: number;
    date_debut: string;
    date_fin: string;
    nb_personnes: number;
    guest_name: string;
    guest_email: string;
    guest_phone: string;
    notes?: string;
    is_deposit?: boolean;
  }) => api.post('/bookings/guest', data),

  pay: (bookingId: number, paymentMethod: string, paymentData?: any) =>
    api.post(`/bookings/${bookingId}/pay`, {
      payment_method: paymentMethod,
      payment_data: paymentData,
    }),

  getMyBookings: (params?: { status?: string; type?: string }) =>
    api.get('/bookings/my', { params }),

  getById: (id: number) => api.get(`/bookings/${id}`),

  cancel: (id: number) => api.post(`/bookings/${id}/cancel`),

  downloadReceipt: (id: number) =>
    api.get(`/bookings/${id}/receipt`, { responseType: 'blob' }),

  // Routes gérant
  getGerantBookings: (params?: {
    apartment_id?: number;
    status?: string;
    from_date?: string;
    to_date?: string;
  }) => api.get('/bookings/gerant/all', { params }),

  getCalendarEvents: (params?: {
    start?: string;
    end?: string;
    apartment_id?: number;
  }) => api.get('/bookings/gerant/calendar', { params }),

  createGerant: (data: any) => api.post('/bookings/gerant/create', data),

  updateGerant: (id: number, data: any) =>
    api.put(`/bookings/gerant/${id}`, data),

  confirmCashPayment: (bookingId: number) =>
    api.post(`/bookings/${bookingId}/confirm-cash`),

  confirmDepositPayment: (bookingId: number) =>
    api.post(`/bookings/${bookingId}/confirm-deposit`),

  // Réservations invitées (gérant/admin)
  createInvitation: (data: {
    apartment_id: number;
    date_debut: string;
    date_fin: string;
    nb_personnes: number;
    prix_total: number;
    guest_name: string;
    guest_email: string;
    guest_phone: string;
    notes?: string;
  }) => api.post('/bookings/invitation/create', data),

  sendInvitation: (bookingId: number) =>
    api.post(`/bookings/invitation/${bookingId}/send`),

  downloadInvoice: (bookingId: number) =>
    api.get(`/bookings/${bookingId}/invoice`, { responseType: 'blob' }),

  deleteBlock: (blockId: number) =>
    api.delete(`/bookings/gerant/block/${blockId}`),

  getStats: () => api.get('/bookings/gerant/stats'),
};

// Services notifications
export const notificationService = {
  getAll: (params?: { page?: number; per_page?: number; unread_only?: boolean }) =>
    api.get('/notifications', { params }),

  getUnreadCount: () => api.get('/notifications/unread-count'),

  markAsRead: (id: number) => api.post(`/notifications/${id}/read`),

  markAllAsRead: () => api.post('/notifications/read-all'),

  delete: (id: number) => api.delete(`/notifications/${id}`),

  clear: () => api.delete('/notifications/clear'),

  subscribePush: (subscription: any) =>
    api.post('/notifications/subscribe', { subscription }),

  unsubscribePush: () => api.post('/notifications/unsubscribe'),
};

// Services messages
export const messageService = {
  getConversations: () => api.get('/messages/conversations'),

  getConversation: (otherUserId: number, params?: {
    apartment_id?: number;
    page?: number;
    per_page?: number;
  }) => api.get(`/messages/conversation/${otherUserId}`, { params }),

  send: (data: {
    receiver_id: number;
    contenu: string;
    apartment_id?: number;
    booking_id?: number;
    sujet?: string;
  }) => api.post('/messages', data),

  contactOwner: (apartmentId: number, data: { contenu: string; sujet?: string }) =>
    api.post(`/messages/apartment/${apartmentId}/contact`, data),

  markAsRead: (id: number) => api.post(`/messages/${id}/read`),

  getUnreadCount: () => api.get('/messages/unread-count'),

  downloadAttachment: (messageId: number) =>
    api.get(`/messages/${messageId}/attachment`, { responseType: 'blob' }),
};

// Services PayPal
export const paypalService = {
  getConfig: () => api.get('/paypal/config'),

  createOrder: (bookingId: number) =>
    api.post(`/paypal/create-order/${bookingId}`),

  captureOrder: (bookingId: number, orderId: string) =>
    api.post(`/paypal/capture-order/${bookingId}`, { order_id: orderId }),

  getOrderStatus: (orderId: string) =>
    api.get(`/paypal/order-status/${orderId}`),
};

// Services pour invitations publiques (sans authentification)
export const invitationService = {
  view: (token: string) =>
    api.get(`/bookings/invitation/view/${token}`),

  confirm: (token: string, data: { payment_method: string }) =>
    api.post(`/bookings/invitation/confirm/${token}`, data),

  createPaypalOrder: (token: string) =>
    api.post(`/bookings/invitation/paypal/${token}/create-order`),

  capturePaypal: (token: string, orderId: string) =>
    api.post(`/bookings/invitation/paypal/${token}/capture`, { order_id: orderId }),
};

// Services admin/gérant
export const adminService = {
  getDashboard: () => api.get('/admin/dashboard'),

  getLocataires: () => api.get('/admin/locataires'),

  createEquipment: (data: { nom: string; icon?: string; categorie?: string }) =>
    api.post('/admin/equipments', data),

  getMonthlyRevenue: () => api.get('/admin/revenue/monthly'),

  getApartmentStats: (apartmentId: number) =>
    api.get(`/admin/apartments/${apartmentId}/stats`),
};

// Services super user
export const superuserService = {
  getDashboard: () => api.get('/superuser/dashboard'),

  getGerants: (status?: string) => 
    api.get('/superuser/gerants', { params: status ? { status } : {} }),

  getLocataires: () => api.get('/superuser/locataires'),

  approveGerant: (gerantId: number) =>
    api.post(`/superuser/gerants/${gerantId}/approve`),

  rejectGerant: (gerantId: number, reason?: string) =>
    api.post(`/superuser/gerants/${gerantId}/reject`, { reason }),

  suspendUser: (userId: number) =>
    api.post(`/superuser/users/${userId}/suspend`),

  activateUser: (userId: number) =>
    api.post(`/superuser/users/${userId}/activate`),

  validateLocataire: (locataireId: number) =>
    api.post(`/superuser/locataires/${locataireId}/validate`),

  updateUser: (userId: number, data: { nom?: string; prenom?: string; email?: string; telephone?: string }) =>
    api.put(`/superuser/users/${userId}`, data),

  getAllApartments: () => api.get('/superuser/apartments'),

  getAllBookings: (params?: { page?: number; per_page?: number }) =>
    api.get('/superuser/bookings', { params }),

  getAllMessages: (params?: { page?: number; per_page?: number; search?: string }) =>
    api.get('/superuser/messages', { params }),

  getAllConversations: () =>
    api.get('/superuser/messages/conversations'),

  getConversationDetail: (user1Id: number, user2Id: number, params?: { page?: number; per_page?: number }) =>
    api.get(`/superuser/messages/conversation/${user1Id}/${user2Id}`, { params }),

  getAllNotifications: (params?: { page?: number; per_page?: number; type?: string }) =>
    api.get('/superuser/notifications', { params }),
};

// Services factures personnalisées
export const invoiceService = {
  // Liste des factures (gérant/admin)
  getAll: () => api.get('/invoices/custom'),

  // Créer une facture
  create: (data: {
    apartment_id: number;
    client_name: string;
    client_email?: string;
    client_phone?: string;
    date_debut?: string;
    date_fin?: string;
    description?: string;
    montant: number;
    is_paid?: boolean;
  }) => api.post('/invoices/custom', data),

  // Récupérer une facture
  get: (id: number) => api.get(`/invoices/custom/${id}`),

  // Télécharger le PDF
  download: (id: number) => 
    api.get(`/invoices/custom/${id}/download`, { responseType: 'blob' }),

  // Envoyer la facture
  send: (id: number, data: {
    send_email?: boolean;
    send_to_space?: boolean;
    recipient_id?: number;
    recipient_email?: string;
  }) => api.post(`/invoices/custom/${id}/send`, data),

  // Mettre à jour une facture
  update: (id: number, data: {
    is_paid?: boolean;
    client_name?: string;
    client_email?: string;
    client_phone?: string;
    description?: string;
    montant?: number;
  }) => api.put(`/invoices/custom/${id}`, data),

  // Supprimer une facture
  delete: (id: number) => api.delete(`/invoices/custom/${id}`),

  // Liste des locataires pour envoi
  getLocataires: () => api.get('/invoices/locataires'),

  // Factures reçues (locataire)
  getReceived: () => api.get('/invoices/received'),

  // Télécharger une facture reçue
  downloadReceived: (id: number) =>
    api.get(`/invoices/received/${id}/download`, { responseType: 'blob' }),
};

export default api;
